/* ═══════════════════════════════════════════════════════════════════════════
   ManDana — the Droplet Film
   ---------------------------------------------------------------------------
   Fifty-seven wordless seconds in nine movements. A droplet leaves the
   comfortable dark of the deep, is called upward, breaks into vapour, finds
   others, travels as part of something larger, and gives itself to dead ground
   — which blooms. Only then does the app say its own name.

   THIS IS THE ARTWORK, PORTED VERBATIM from the Claude Design project's
   droplet-film.jsx. Same constants, same choreography, same palette, same
   seeded randomness, frame for frame. What did NOT come across is the engine it
   was authored on: a scrub bar, a playhead saved to storage, a video exporter
   and a write-back channel to the design canvas. See app/md-film.ts for why
   none of that could ship and what replaced it.

   Two edits, both forced and both listed here so nobody has to diff to find
   them:
     • The mark loads from /mandana-mark.svg (public/) rather than the design
       project's ./assets/, and carries an alt.
     • `glow` was a design-panel slider. It ships at the panel's own default, 1.

   Everything is one tree rendered as a pure function of T. Nothing mounts or
   unmounts at a movement boundary — that is what lets one droplet drift, spiral,
   orbit and fall as a single continuous thing instead of nine cuts.
   ═══════════════════════════════════════════════════════════════════════════ */

const { mdAnimate, mdEasing, mdClamp } = window;

const MD_FILM_C = {
  pearl: '#FAF9FC', purple: '#7B5BC0', amethyst: '#5B3E9B',
  lavender: '#C3A9F0', halo: '#A585DC', dim: '#3A2C5E', dead: '#241C38',
};

/* the three motion helpers */
const mdGo = (from, to, start, end) => mdAnimate({ from, to, start, end, ease: mdEasing.easeInOutCubic });
const mdPop = (from, to, start, end) => mdAnimate({ from, to, start, end, ease: mdEasing.easeOutBack });
const mdDrop = (from, to, start, end) => mdAnimate({ from, to, start, end, ease: mdEasing.easeInCubic });
const mdSway = (T, f, a, ph = 0) => Math.sin(T * f + ph) * a;

/* Seeded, so the stars sit in the same places on every device and every visit.
   A film whose randomness moves between loads is a film nobody can review. */
function mdFilmRng(seed) {
  let s = (seed * 2654435761) >>> 0 || 1;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

const MD_STARS = Array.from({ length: 30 }, (_, i) => {
  const r = mdFilmRng(i + 77);
  return { x: 60 + r() * 1800, y: 40 + r() * 560, sz: 1.5 + r() * 2, f: 0.5 + r() * 1.2, ph: r() * 6.28, o: 0.10 + r() * 0.22 };
});
const MD_PARTS = Array.from({ length: 12 }, (_, i) => {
  const r = mdFilmRng(i + 41);
  return { ph: (i / 12) * Math.PI * 2, wob: 0.6 + r() * 1.2, lag: r() * 0.1, sz: 6 + r() * 4 };
});
const MD_SEGN = 14;
const MD_SEGW = 1920 / MD_SEGN;
const MD_SEGS = Array.from({ length: MD_SEGN }, (_, i) => {
  const r = mdFilmRng(i + 9);
  const d = Math.abs(i - (MD_SEGN - 1) / 2) / ((MD_SEGN - 1) / 2);
  const w = 1 - d * 0.75;
  return { rot: (r() - 0.5) * 14 * w, dy: (r() - 0.5) * 16 * w, d };
});
const MD_RISERS = Array.from({ length: 7 }, (_, i) => {
  const r = mdFilmRng(i + 23);
  return { dx: (r() - 0.5) * 260, d: r() * 2.2, h: 120 + r() * 220, sz: 3 + r() * 3 };
});

function MdDot({ x, y, r = 13, o = 1, glow = 1, stretch = 1, g = 1 }) {
  if (o <= 0.001) return null;
  return (
    <div style={{ position: 'absolute', left: x, top: y, width: r * 2, height: r * 2, margin: -r, transform: `scaleY(${stretch})`, opacity: o, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', inset: -r * 1.6, borderRadius: '50%', background: `radial-gradient(circle, ${MD_FILM_C.halo}52, transparent 66%)`, opacity: glow * g }} />
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: `radial-gradient(circle at 38% 32%, #FFFFFF, ${MD_FILM_C.pearl} 52%, ${MD_FILM_C.lavender})`, boxShadow: `0 0 ${18 * g}px ${MD_FILM_C.halo}66` }} />
    </div>
  );
}

function MdRing({ x, y, r, o, color = MD_FILM_C.halo, w = 1, g = 0, rot = 0 }) {
  if (o <= 0.001 || r <= 0) return null;
  return <div style={{ position: 'absolute', left: x - r, top: y - r, width: r * 2, height: r * 2, borderRadius: '50%', border: `${w}px solid ${color}`, opacity: o, transform: `rotate(${rot}deg)`, boxShadow: g > 0 ? `0 0 ${g}px ${MD_FILM_C.halo}55, inset 0 0 ${g}px ${MD_FILM_C.halo}33` : 'none', pointerEvents: 'none' }} />;
}

/**
 * The film. Draws the frame at authored time `t` on a 1920×1080 stage; the
 * caller scales that stage to the viewport (app/md-landing.jsx).
 *
 * `cues` is the cue table from mdFilmCues — every moment below is expressed
 * relative to a movement's start rather than as an absolute second, so
 * retiming a movement moves everything keyed to it.
 */
function MdDropletFilm({ t: T, cues, glow = 1 }) {
  const c = cues;
  const g = glow;

  /* ---- key moments ---- */
  const riseStart = c.TheCall + 3.0, fragT = c.Vapor + 0.3, formT = c.Communion + 0.8,
    detachT = c.TheFall + 0.6, impactT = c.TheFall + 2.6;

  /* ---- abyss: breathing rings + hero ---- */
  const heroRestY = 720;
  const heroRiseY = mdGo(heroRestY, 340, riseStart, c.Vapor);
  const tremble = mdGo(0, 1, c.TheCall + 0.8, c.TheCall + 2.6)(T) * mdGo(1, 0, c.TheCall + 2.7, riseStart)(T);
  const abyssFade = mdGo(1, 0, c.TheCall + 2.2, c.TheCall + 4.6)(T);
  const heroPreY = heroRiseY(T) + mdSway(T, 0.7, 14) * mdGo(1, 0.2, c.TheCall, c.Vapor)(T) + mdSway(T, 19, 5) * tremble;
  const heroPreX = 960 + mdSway(T, 0.5, 8) * mdGo(1, 0.2, c.TheCall, c.Vapor)(T) + mdSway(T, 23, 4) * tremble;

  /* ---- vapor spiral ---- */
  const vapP = mdClamp((T - fragT) / ((formT + 1.2) - fragT), 0, 1);
  const shake = Math.sin(vapP * Math.PI) * 6;

  /* ---- the circle (cloud as geometry) ---- */
  const CX = 960, CY = 410;
  const joins = mdGo(0, 1, c.Communion + 2.2, c.Communion + 3.0)(T)
    + mdGo(0, 1, c.Communion + 3.2, c.Communion + 4.0)(T)
    + mdGo(0, 1, c.Communion + 4.2, c.Communion + 5.0)(T);
  const merge = mdGo(0, 1, c.Journey + 2.4, c.Journey + 4.4)(T);
  const R = 70 + joins * 15 + merge * 38;
  const circleO = mdGo(0, 1, formT + 0.6, formT + 2.2)(T) * mdGo(1, 0, c.Rebirth + 3.5, c.Rebirth + 5.5)(T);
  /* How far the world has passed beneath the circle, in px — 0 until the
     journey begins, 3300 once the giza group has crossed. One function, so the
     pyramids' drift and the rim's roll below cannot disagree. */
  const worldRun = (t) => 3300 * mdClamp((t - (c.Journey - 0.6)) / ((c.DeadLand + 1.8) - (c.Journey - 0.6)), 0, 1);
  /* 2026-08-17, on Marwan's note: while the world passes, the circle ROLLS
     over it instead of idling — rim arc equals ground distance (θ = s / R), so
     the companions revolve at exactly the pyramids' pace. The resting spin
     stays underneath it; the counter-rotating core and the arriving second
     circle are separate bodies and keep their own quiet motion. */
  const orbitIdle = T * 0.35;
  const orbitRot = orbitIdle + worldRun(T) / R;
  /* second circle gliding in */
  const c2x = mdGo(2350, CX + R * 0.4, c.Journey + 0.3, c.Journey + 4.2)(T);
  const c2o = mdGo(0, 0.8, c.Journey + 0.5, c.Journey + 1.8)(T) * (1 - merge);

  /* ---- hero on the orbit, then the fall ---- */
  const tOrbit = Math.min(T, detachT);
  /* starts at bottom of circle; rides the same roll as the rim it sits on */
  const heroAng = Math.PI / 2 + tOrbit * 0.35 + worldRun(tOrbit) / R;
  const orbX = CX + Math.cos(heroAng) * R;
  const orbY = CY + Math.sin(heroAng) * R + mdSway(tOrbit, 0.8, 4);
  const fallY = mdDrop(orbY, 848, detachT, impactT)(T);
  const heroPostY = T < detachT ? orbY : fallY;
  const heroPostX = T < detachT ? orbX : orbX + mdGo(0, (960 - orbX), detachT, impactT - 0.6)(T);
  const hstr = 1 + mdGo(0, 0.5, detachT + 0.3, impactT)(T) * (T < impactT ? 1 : 0);
  const heroO = T < formT
    ? mdGo(1, 0, c.Vapor, fragT + 0.4)(T)
    : (T >= impactT ? 0 : mdGo(0, 1, formT + 0.8, formT + 1.8)(T));
  const hx = T < formT ? heroPreX : heroPostX;
  const hy = T < formT ? heroPreY : heroPostY;

  /* ---- horizon: one line that fractures, then heals in light ---- */
  const horizonO = mdGo(0, 0.38, c.Journey, c.Journey + 2)(T);
  const frac = mdGo(0, 1, c.DeadLand + 0.5, c.DeadLand + 3.2)(T);

  /* ---- world geometry passing (journey) ---- */
  const worldO = mdGo(0, 1, c.Journey + 1, c.Journey + 3)(T) * mdGo(1, 0, c.DeadLand + 1.4, c.DeadLand + 3.2)(T);
  /* the Giza group: constant, unhurried drift — fully in from the right, fully
     out left. Driven by the same worldRun the rim rolls to. */
  const gp = worldRun(T) / 3300;
  const gizaX = 1980 - worldRun(T);
  const gizaO = Math.min(1, Math.sin(gp * Math.PI) * 2.4);

  /* ---- rebirth ---- */
  const bloomC = { x: 960, y: 848 };
  const roseO = mdGo(0, 1, c.Rebirth + 1.2, c.Rebirth + 2.4)(T) * mdGo(1, 0, c.Finale - 1.2, c.Finale)(T);
  const roseRot = (T - c.Rebirth) * 4;

  /* ---- finale: logo ----
     The design file faded the whole piece out at the very end unless `hold` was
     set. The landing always holds — there is nothing after this frame to cut
     to — so the fade is gone rather than left as a branch nothing takes. */
  const markO = mdGo(0, 1, c.Finale + 0.6, c.Finale + 3)(T);
  const wordO = mdGo(0, 1, c.Finale + 2.2, c.Finale + 4)(T);
  const fadeAll = mdGo(1, 0, c.Finale - 0.8, c.Finale)(T); /* everything else gone by finale */

  return (
    <div style={{ position: 'absolute', inset: 0, background: '#000', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: fadeAll, transform: `translate(${mdSway(T, 13, shake)}px, ${mdSway(T, 11, shake, 2)}px)` }}>

        {/* stars — pinpoints only */}
        {MD_STARS.map((st, i) => (
          <div key={'s' + i} style={{ position: 'absolute', left: st.x, top: st.y, width: st.sz, height: st.sz, borderRadius: '50%', background: MD_FILM_C.pearl, opacity: st.o * (0.6 + 0.4 * Math.sin(T * st.f + st.ph)) * mdGo(0, 1, c.Vapor + 2, c.Journey)(T) }} />
        ))}

        {/* abyss: breathing concentric rings around the resting droplet */}
        {[92, 158, 236].map((r0, i) => (
          <MdRing key={'a' + i} x={960} y={heroRestY} r={r0 * (1 + 0.03 * Math.sin(T * 0.6 + i * 1.7))} o={abyssFade * mdGo(0, 0.62 - i * 0.13, 0.6 + i * 0.9, 2.4 + i * 0.9)(T)} color={MD_FILM_C.halo} w={1.5} g={7 * g} />
        ))}

        {/* the call: one thin descending line */}
        <div style={{ position: 'absolute', left: 959, top: 0, width: 2, height: mdGo(0, heroPreY - 26, c.TheCall, c.TheCall + 2.2)(T), background: `linear-gradient(180deg, ${MD_FILM_C.pearl}99, ${MD_FILM_C.halo}33)`, opacity: mdGo(0, 1, c.TheCall, c.TheCall + 1)(T) * mdGo(1, 0, c.Vapor + 0.8, c.Vapor + 2.2)(T) }} />

        {/* vapor: the droplet becomes a rising spiral of points */}
        {MD_PARTS.map((p, i) => {
          const pp = mdClamp(vapP - p.lag, 0, 1);
          if (pp <= 0 || pp >= 1) return null;
          const a = Math.sin(pp * Math.PI);
          const yc = 340 - 150 * a + (CY - 340) * pp;
          const r = 16 + 54 * pp + 70 * a;
          const ang = p.ph + T * (0.9 + 0.5 * p.wob);
          const po = mdGo(0, 1, fragT, fragT + 0.5)(T) * mdGo(1, 0, formT + 0.4, formT + 1.2)(T);
          return <MdDot key={'p' + i} x={960 + Math.cos(ang) * r} y={yc + Math.sin(ang) * r * 0.55 + mdSway(T, p.wob * 3, 6 * a, i)} r={p.sz / 2} o={po} glow={0.6} g={g} />;
        })}

        {/* the great circle — the gathering */}
        <MdRing x={CX} y={CY} r={R} o={circleO * 0.5} color={MD_FILM_C.halo} g={10 * g} />
        <MdRing x={CX} y={CY} r={R * 0.62} o={circleO * 0.3 * merge} color={MD_FILM_C.dim} />
        {/* orbiting companions on the circle */}
        {Array.from({ length: 11 }, (_, i) => {
          const ang = (i + 1) / 12 * Math.PI * 2 + Math.PI / 2 + orbitRot;
          const show = circleO * mdGo(0, 1, formT + 1.4 + i * 0.12, formT + 2.6 + i * 0.12)(T);
          return <MdDot key={'o' + i} x={CX + Math.cos(ang) * R} y={CY + Math.sin(ang) * R} r={4.5} o={show * 0.9} glow={0.35} g={g} />;
        })}
        {/* inner orbit after the merge */}
        {Array.from({ length: 8 }, (_, i) => {
          const ang = i / 8 * Math.PI * 2 - orbitIdle * 1.4;
          return <MdDot key={'q' + i} x={CX + Math.cos(ang) * R * 0.62} y={CY + Math.sin(ang) * R * 0.62} r={3} o={circleO * merge * 0.7} glow={0.25} g={g} />;
        })}

        {/* second circle arriving to merge */}
        <MdRing x={c2x} y={CY - 40 + mdSway(T, 0.6, 8, 2)} r={64} o={c2o * 0.45} color={MD_FILM_C.halo} />
        {Array.from({ length: 6 }, (_, i) => {
          const ang = i / 6 * Math.PI * 2 + orbitIdle * 1.2;
          return <MdDot key={'w' + i} x={c2x + Math.cos(ang) * 64} y={CY - 40 + mdSway(T, 0.6, 8, 2) + Math.sin(ang) * 64} r={3.5} o={c2o * 0.8} glow={0.3} g={g} />;
        })}

        {/* fellow droplets gliding in along faint chords */}
        {[{ sx: 220, sy: 200, t0: c.Communion + 1.4 }, { sx: 1740, sy: 150, t0: c.Communion + 2.4 }, { sx: 380, sy: 900, t0: c.Communion + 3.4 }].map((f, i) => {
          const q = mdGo(0, 1, f.t0, f.t0 + 1.5)(T);
          if (q <= 0.001 || q >= 0.985) return null;
          const fx = f.sx + (CX - f.sx) * q, fy = f.sy + (CY - f.sy) * q;
          const ang = Math.atan2(CY - f.sy, CX - f.sx) * 180 / Math.PI;
          return (
            <div key={'f' + i}>
              <div style={{ position: 'absolute', left: fx, top: fy, width: 70, height: 1, background: `linear-gradient(90deg, ${MD_FILM_C.halo}44, transparent)`, transform: `rotate(${ang + 180}deg)`, transformOrigin: '0 0', opacity: 0.6 * (1 - q) }} />
              <MdDot x={fx} y={fy} r={6} o={Math.min(1, q * 5)} glow={0.5} g={g} />
            </div>
          );
        })}

        {/* passing world — thin outlined geometry on the horizon */}
        <div style={{ opacity: worldO }}>
          {/* far parallax layer */}
          {[{ x0: 2300, spd: 64, kind: 'tri', w: 460, h: 110 }, { x0: 3200, spd: 64, kind: 'dome', w: 220, h: 100 }, { x0: 4000, spd: 64, kind: 'tri', w: 400, h: 130 }, { x0: 4700, spd: 64, kind: 'line', w: 1, h: 170 }].map((L, i) => {
            const lx = L.x0 - L.spd * Math.max(0, T - c.Journey);
            if (lx < -700 || lx > 2300) return null;
            if (L.kind === 'tri') return (
              <div key={'l' + i} style={{ position: 'absolute', left: lx, top: 850 - L.h, width: L.w, height: L.h, clipPath: 'polygon(50% 0, 100% 100%, 0 100%)', background: MD_FILM_C.halo, opacity: 0.18 }}>
                <div style={{ position: 'absolute', inset: 1, clipPath: 'polygon(50% 0, 100% 100%, 0 100%)', background: '#000' }} />
              </div>);
            if (L.kind === 'dome') return <div key={'l' + i} style={{ position: 'absolute', left: lx, top: 850 - L.h, width: L.w, height: L.h, border: `1px solid ${MD_FILM_C.halo}`, borderBottom: 'none', borderRadius: `${L.w}px ${L.w}px 0 0`, opacity: 0.20 }} />;
            return <div key={'l' + i} style={{ position: 'absolute', left: lx, top: 850 - L.h, width: 1, height: L.h, background: MD_FILM_C.halo, opacity: 0.22 }} />;
          })}

          {/* the Giza pass — three pyramids, a quiet constant drift */}
          <div style={{ position: 'absolute', left: gizaX, top: 0, width: 1240, height: 1080, opacity: gizaO }}>
            {[{ x: 0, w: 400, h: 238 }, { x: 320, w: 320, h: 192 }, { x: 580, w: 216, h: 130 }].map((P, i) => (
              <div key={'g' + i} style={{ position: 'absolute', left: P.x, top: 850 - P.h, width: P.w, height: P.h, clipPath: 'polygon(50% 0, 100% 100%, 0 100%)', background: MD_FILM_C.halo, opacity: 0.42 - i * 0.05 }}>
                <div style={{ position: 'absolute', inset: 1, clipPath: 'polygon(50% 0, 100% 100%, 0 100%)', background: '#000' }} />
              </div>
            ))}
          </div>
        </div>

        {/* the horizon — one line that fractures, then heals in light */}
        {MD_SEGS.map((s, i) => {
          const heal = mdGo(0, 1, c.Rebirth + 0.6 + s.d * 1.6, c.Rebirth + 1.8 + s.d * 1.6)(T);
          const rot = s.rot * frac * (1 - heal);
          const dy = s.dy * frac * (1 - heal);
          const lit = heal;
          return <div key={'h' + i} style={{ position: 'absolute', left: i * MD_SEGW, top: 849.5 + dy, width: MD_SEGW + 1, height: 1, transform: `rotate(${rot}deg)`, background: lit > 0.01 ? MD_FILM_C.lavender : MD_FILM_C.dead, opacity: horizonO / 0.38 * (0.38 + 0.45 * lit), boxShadow: lit > 0.05 ? `0 0 ${10 * lit * g}px ${MD_FILM_C.halo}` : 'none' }} />;
        })}

        {/* hero */}
        <MdDot x={hx} y={hy} r={13} o={heroO} stretch={hstr} glow={0.9 + 0.2 * Math.sin(T * 1.3)} g={g} />

        {/* impact rings — perfect circles rippling from the point of giving */}
        {[0, 0.35, 0.7].map((d, i) => {
          const p = mdGo(0, 1, impactT + d, impactT + 1.8 + d)(T);
          if (p <= 0.01 || p >= 0.99) return null;
          return <MdRing key={'r' + i} x={bloomC.x} y={bloomC.y} r={12 + p * 250} o={(1 - p) * 0.55} color={MD_FILM_C.pearl} />;
        })}

        {/* rebirth rosette — six circles around one */}
        <div style={{ position: 'absolute', left: bloomC.x, top: bloomC.y, opacity: roseO, transform: `rotate(${roseRot}deg)` }}>
          {Array.from({ length: 6 }, (_, k) => {
            const p = mdPop(0, 1, c.Rebirth + 1.4 + k * 0.28, c.Rebirth + 2.6 + k * 0.28)(T);
            const px = Math.cos(k / 6 * Math.PI * 2) * 54, py = Math.sin(k / 6 * Math.PI * 2) * 54;
            return <div key={'b' + k} style={{ position: 'absolute', left: px - 54, top: py - 54, width: 108, height: 108, borderRadius: '50%', border: `1px solid ${MD_FILM_C.lavender}`, transform: `scale(${p})`, opacity: 0.6, boxShadow: `0 0 ${8 * g}px ${MD_FILM_C.halo}44` }} />;
          })}
          <div style={{ position: 'absolute', left: -54, top: -54, width: 108, height: 108, borderRadius: '50%', border: `1px solid ${MD_FILM_C.pearl}`, transform: `scale(${mdPop(0, 1, c.Rebirth + 1.2, c.Rebirth + 2.2)(T)})`, opacity: 0.8 }} />
          <div style={{ position: 'absolute', left: -112, top: -112, width: 224, height: 224, borderRadius: '50%', border: `1px solid ${MD_FILM_C.halo}`, transform: `scale(${mdGo(0, 1, c.Rebirth + 3, c.Rebirth + 4.4)(T)})`, opacity: 0.4 }} />
        </div>

        {/* life rising — tiny points ascending from the bloom */}
        {MD_RISERS.map((rr, i) => {
          const p = mdGo(0, 1, c.Rebirth + 2.6 + rr.d, c.Rebirth + 5.6 + rr.d)(T);
          if (p <= 0.01 || p >= 0.99) return null;
          return <MdDot key={'v' + i} x={bloomC.x + rr.dx + mdSway(T, 1.2, 10, i)} y={bloomC.y - 20 - p * rr.h} r={rr.sz / 2} o={Math.sin(p * Math.PI) * 0.8} glow={0.4} g={g} />;
        })}
      </div>

      {/* finale — the logo, alone in total black */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ position: 'relative', opacity: markO }}>
          <div style={{ position: 'absolute', inset: -90, borderRadius: '50%', background: `radial-gradient(circle, ${MD_FILM_C.halo}3E, transparent 66%)`, transform: `scale(${mdGo(0.7, 1, c.Finale + 0.6, c.Finale + 3.4)(T)})` }} />
          <img src="/mandana-mark.svg" alt="" style={{ height: 150, display: 'block', position: 'relative', filter: `drop-shadow(0 0 26px ${MD_FILM_C.halo}77)` }} />
        </div>
        <div style={{ marginTop: 26, opacity: wordO, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 44, letterSpacing: '0.14em', color: MD_FILM_C.pearl, textIndent: '0.14em' }}>ManDana</div>
        <div style={{ marginTop: 10, opacity: wordO, fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, letterSpacing: '0.22em', color: '#9B7BD4', textIndent: '0.22em' }}>A MUSLIM&#39;S LIFE IN CONTEXT</div>
      </div>
    </div>
  );
}

Object.assign(window, { MdDropletFilm });
